import {
  createProfessionalExporter,
  getExportFileDate,
  getExportTimestamp,
} from '../../../utils/professionalExport';
import { getOwnerExportBrand, getOwnerExportSlug } from '../../../utils/gymExportBrand';
import { formatPrice } from './reportsData';
import { downloadCsv, toCsvRow } from '../../../utils/downloadCsv';

const date = () => getExportFileDate();

const subscriptionExporter = createProfessionalExporter({
  title: 'Subscription Report',
  headers: ['Plan', 'Active Count', 'Monthly', 'Quarterly', 'Yearly', 'Revenue'],
  slug: 'subscription-report',
  brand: 'owner',
  getRows: (list) =>
    list.map((row) => [
      row.plan,
      row.activeCount,
      row.monthlyCount,
      row.quarterlyCount,
      row.yearlyCount,
      formatPrice(row.revenue),
    ]),
  getSummary: (list) => [
    `Total plans: ${list.length}`,
    `Active subscriptions: ${list.reduce((sum, r) => sum + r.activeCount, 0)}`,
  ],
});

const revenueExporter = createProfessionalExporter({
  title: 'Revenue Report',
  headers: ['Month', 'Subscriptions', 'Renewals', 'New Signups', 'Total'],
  slug: 'revenue-report',
  brand: 'owner',
  getRows: (list) =>
    list.map((row) => [
      row.month,
      formatPrice(row.subscriptions),
      formatPrice(row.renewals),
      formatPrice(row.newSignups),
      formatPrice(row.total),
    ]),
  getSummary: (list) => [
    `Total months: ${list.length}`,
    `Total revenue: ${formatPrice(list.reduce((sum, r) => sum + r.total, 0))}`,
  ],
});

function buildCombinedCsv(subscriptionData, revenueData) {
  const brand = getOwnerExportBrand();
  return [
    brand.name,
    brand.branch,
    'Platform Reports',
    `Generated: ${getExportTimestamp()}`,
    '',
    'Subscription Report',
    toCsvRow(['Plan', 'Active Count', 'Monthly', 'Quarterly', 'Yearly', 'Revenue']),
    ...subscriptionData.map((r) =>
      toCsvRow([
        r.plan,
        r.activeCount,
        r.monthlyCount,
        r.quarterlyCount,
        r.yearlyCount,
        formatPrice(r.revenue),
      ]),
    ),
    '',
    'Revenue Report',
    toCsvRow(['Month', 'Subscriptions', 'Renewals', 'New Signups', 'Total']),
    ...revenueData.map((r) =>
      toCsvRow([
        r.month,
        formatPrice(r.subscriptions),
        formatPrice(r.renewals),
        formatPrice(r.newSignups),
        formatPrice(r.total),
      ]),
    ),
  ].join('\n');
}

function buildCombinedHtml(subscriptionData, revenueData) {
  const brand = getOwnerExportBrand();
  const subHeaders = ['Plan', 'Active Count', 'Monthly', 'Quarterly', 'Yearly', 'Revenue'];
  const revHeaders = ['Month', 'Subscriptions', 'Renewals', 'New Signups', 'Total'];

  const subRows = subscriptionData
    .map(
      (r) =>
        `<tr><td>${r.plan}</td><td>${r.activeCount}</td><td>${r.monthlyCount}</td><td>${r.quarterlyCount}</td><td>${r.yearlyCount}</td><td>${formatPrice(r.revenue)}</td></tr>`,
    )
    .join('');

  const revRows = revenueData
    .map(
      (r) =>
        `<tr><td>${r.month}</td><td>${formatPrice(r.subscriptions)}</td><td>${formatPrice(r.renewals)}</td><td>${formatPrice(r.newSignups)}</td><td>${formatPrice(r.total)}</td></tr>`,
    )
    .join('');

  return `<!DOCTYPE html>
<html lang="en"><head><meta charset="utf-8"/><title>Platform Reports - ${brand.name}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 0; padding: 28px; color: #1e293b; }
  .header { border-bottom: 3px solid #6bb800; padding-bottom: 16px; margin-bottom: 22px; }
  .brand { font-size: 26px; font-weight: 700; color: #6bb800; margin: 0 0 6px; }
  .meta { font-size: 13px; color: #64748b; line-height: 1.65; }
  h2 { font-size: 18px; margin: 24px 0 12px; color: #0f172a; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; margin-bottom: 8px; }
  th { background: #6bb800; color: #fff; text-align: left; padding: 10px 12px; }
  td { padding: 9px 12px; border-bottom: 1px solid #e2e8f0; }
  tr:nth-child(even) td { background: #f8fafc; }
  .footer { margin-top: 28px; padding-top: 14px; border-top: 1px solid #e2e8f0; font-size: 12px; color: #94a3b8; text-align: center; }
  @media print { body { padding: 12px; } @page { margin: 12mm; } }
</style></head><body>
  <div class="header">
    <p class="brand">${brand.name}</p>
    <div class="meta">${brand.branch}</div>
  </div>
  <h2>Subscription Report</h2>
  <table><thead><tr>${subHeaders.map((h) => `<th>${h}</th>`).join('')}</tr></thead><tbody>${subRows}</tbody></table>
  <h2>Revenue Report</h2>
  <table><thead><tr>${revHeaders.map((h) => `<th>${h}</th>`).join('')}</tr></thead><tbody>${revRows}</tbody></table>
  <div class="footer">${brand.name} — Confidential Business Report · ${getExportTimestamp()}</div>
</body></html>`;
}

function openCombinedWindow(subscriptionData, revenueData, { autoPrint = false } = {}) {
  const html = buildCombinedHtml(subscriptionData, revenueData);
  const win = window.open('', '_blank');
  if (!win) return false;
  win.document.open();
  win.document.write(html);
  win.document.close();
  win.focus();
  if (autoPrint) {
    const triggerPrint = () => win.print();
    win.onload = triggerPrint;
    window.setTimeout(triggerPrint, 600);
  }
  return true;
}

function downloadBlob(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => URL.revokeObjectURL(url), 200);
}

function reportFileName(ext) {
  return `platform-reports-${getOwnerExportSlug()}-${date()}.${ext}`;
}

export function printReports(subscriptionData, revenueData) {
  openCombinedWindow(subscriptionData, revenueData, { autoPrint: true });
}

export function exportReportsCsv(subscriptionData, revenueData) {
  downloadCsv(buildCombinedCsv(subscriptionData, revenueData), reportFileName('csv'));
}

export function exportReportsExcel(subscriptionData, revenueData) {
  const html = buildCombinedHtml(subscriptionData, revenueData);
  downloadBlob(`\ufeff${html}`, reportFileName('xls'), 'application/vnd.ms-excel;charset=utf-8;');
}

export function exportReportsPdf(subscriptionData, revenueData) {
  openCombinedWindow(subscriptionData, revenueData, { autoPrint: false });
}

export function exportAllReports(subscriptionData, revenueData) {
  exportReportsCsv(subscriptionData, revenueData);
}

export function exportSubscriptionReport(data) {
  subscriptionExporter.csv(data);
}

export function printSubscriptionReport(data) {
  subscriptionExporter.print(data);
}

export function exportRevenueReport(data) {
  revenueExporter.csv(data);
}

export function printRevenueReport(data) {
  revenueExporter.print(data);
}
